import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { availableNavigation } from '../config/dashboardNavigation';
import { useCellFeatures } from '../contexts/CellFeaturesContext';
import { useOptionalTenant } from '../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { CommandBar } from './CommandBar';
import { ThemeToggle } from './ThemeToggle';
import { navigationTranslations } from '../utils/translations';

type FormInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function isUnsavedInput(input: EventTarget | null): input is FormInput {
  if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement)) return false;
  // Header controls (including the tenant select) and search only change navigation.
  if (input.closest('[data-navigation-header], [role="search"]') || (input instanceof HTMLInputElement && input.type === 'search')) return false;
  // Reuse the existing editor boundary as well as native forms and explicit opt-ins.
  return !!input.form || !!input.closest('[data-unsaved-guard], [data-panel-close-guard]');
}

const t = navigationTranslations.de;

export function Navigation() {
  const { user, logout } = useAuth();
  const tenant = useOptionalTenant();
  const header = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const measure = () => setHeaderHeight(header.current?.getBoundingClientRect().height ?? 0);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (header.current) observer?.observe(header.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [user, tenant?.runtime.tenantId]);
  const navigate = useNavigate();
  const originalInputs = useRef(new Map<FormInput, string>());
  const [tenantSwitchNotice, setTenantSwitchNotice] = useState<string | null>(null);
  useEffect(() => {
    const remember = (event: FocusEvent) => {
      const input = event.target;
      if (isUnsavedInput(input) && !originalInputs.current.has(input)) originalInputs.current.set(input, input.value);
    };
    document.addEventListener('focusin', remember);
    return () => document.removeEventListener('focusin', remember);
  }, []);
  const changeTenant = async (id: string) => {
    setTenantSwitchNotice(null);
    const dirty = [...originalInputs.current].some(([input, value]) => input.isConnected && isUnsavedInput(input) && input.value !== value);
    if (dirty && !window.confirm(t.confirmTenantSwitch)) {
      setTenantSwitchNotice(t.tenantSwitchUnconfirmed);
      return;
    }
    try { if (tenant) { await tenant.switchTenant(id); navigate('/'); } } catch { /* Provider displays the error. */ }
  };
  const features = useCellFeatures();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const morePanel = useRef<HTMLElement>(null);
  useEffect(() => { if (moreOpen) morePanel.current?.querySelector<HTMLAnchorElement>('a')?.focus(); }, [moreOpen]);
  const moreButton = useRef<HTMLButtonElement>(null);
  useEffect(() => { setMoreOpen(false); }, [location.pathname, location.search]);
  if (!user || !tenant) return null;
  const { runtime, reachable } = tenant;
  const areas = availableNavigation(user.role, features);
  const activeArea = areas.find(area => area.routes.some(route => route.path.split('?')[0] === location.pathname
    || (route.path !== '/' && location.pathname.startsWith(`${route.path}/`))
    || (route.path === '/' && location.pathname === '/dashboard')));
  const routeActive = (path: string) => {
    const [pathname, search] = path.split('?');
    if (search) return pathname === location.pathname && new URLSearchParams(search).get('view') === (new URLSearchParams(location.search).get('view') ?? 'offen');
    return pathname === location.pathname || (pathname === '/' && location.pathname === '/dashboard') || (pathname !== '/' && location.pathname.startsWith(`${pathname}/`));
  };
  const linkClass = (active: boolean) => `rounded-ctl px-2 py-1 text-[13px] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-signal ${active ? 'bg-ink text-panel' : 'text-muted hover:bg-line-2'}`;
  const handleLogout = async () => {
    setLogoutError(null);
    try { await logout(); } catch (error) { setLogoutError(error instanceof Error ? error.message : 'Abmelden fehlgeschlagen'); }
  };
  return <header data-navigation-header ref={header} style={{ '--navigation-height': `${headerHeight}px` } as CSSProperties} className="relative shrink-0 border-b border-line bg-panel text-ink">
    <div className="flex min-h-12 flex-wrap items-center gap-2 px-3 py-1">
      <Link to="/" className="text-base font-extrabold tracking-tight">roomaite</Link>
      {reachable.length > 1 ? <select aria-label="Mandant" value={runtime.tenantId} onChange={event => void changeTenant(event.target.value)} className="max-w-32 rounded-ctl border border-line bg-panel text-sm">
        {reachable.map(tenant => <option key={tenant.tenantId} value={tenant.tenantId}>{tenant.displayName}</option>)}
      </select> : <span className="max-w-32 truncate text-sm text-muted">{runtime.displayName}</span>}
      <CommandBar mode="search" />
      <ThemeToggle />
      <button onClick={handleLogout} aria-label="Abmelden" className="ml-auto rounded-ctl p-1.5 text-muted hover:bg-line-2">
        <LogOut size={16} strokeWidth={1.75} />
      </button>
    </div>
    {tenantSwitchNotice && <p role="alert" className="px-3 text-sm text-warn">{tenantSwitchNotice}</p>}
    {logoutError && <p role="alert" className="px-3 text-sm text-crit">Abmelden: {logoutError}</p>}
    <aside aria-label="Seitenleiste" className="hidden border-t border-line bg-panel p-3 md:block lg:absolute lg:left-0 lg:top-full lg:h-[calc(100dvh-var(--navigation-height))] lg:w-52 lg:overflow-y-auto lg:border-r lg:border-t-0">
      <nav aria-label="Bereiche" className="flex flex-wrap gap-3 lg:block lg:space-y-3">
        {areas.map(area => <div key={area.label}>
          {area.routes.length === 1 ? <Link to={area.routes[0].path} aria-current={activeArea?.label === area.label ? 'page' : undefined}
            className={`block ${linkClass(activeArea?.label === area.label)}`}>{area.label}</Link>
            : <p aria-current={activeArea?.label === area.label ? 'true' : undefined} className={`rounded-ctl px-2 py-1 text-[13px] font-medium ${activeArea?.label === area.label ? 'bg-ink text-panel' : 'text-muted'}`}>{area.label}</p>}
          {area.routes.length > 1 && <div role="group" aria-label={`${area.label}: Unterpunkte`} className="mt-1 space-y-1 pl-3">
            {area.routes.map(route => <Link key={route.path} to={route.path} className={`block ${linkClass(routeActive(route.path))}`}
              aria-current={routeActive(route.path) ? 'page' : undefined}>{route.label}</Link>)}
          </div>}
        </div>)}
      </nav>
    </aside>
    <nav aria-label="Mobile Bereiche" className="fixed inset-x-0 bottom-0 flex h-[calc(48px+env(safe-area-inset-bottom))] items-start justify-around border-t border-line bg-panel pt-2 pb-[env(safe-area-inset-bottom)] md:hidden">
      {areas.filter(area => ['Heute', 'Aufgaben', 'Reinigung'].includes(area.label)).map(area => <Link key={area.label}
        to={area.routes[0].path} className={linkClass(activeArea?.label === area.label)} aria-current={activeArea?.label === area.label ? 'page' : undefined}>{area.label}</Link>)}
      <button ref={moreButton} aria-expanded={moreOpen} aria-controls="mobile-more" className={linkClass(moreOpen)} onClick={() => setMoreOpen(value => !value)}>Mehr</button>
    </nav>
    {moreOpen && <nav ref={morePanel} id="mobile-more" aria-label="Weitere Bereiche" onKeyDown={event => {
      if (event.key === 'Escape') { setMoreOpen(false); moreButton.current?.focus(); }
    }} className="fixed inset-x-2 bottom-[calc(56px+env(safe-area-inset-bottom))] max-h-[70dvh] overflow-auto rounded-panel border border-line bg-panel p-3 md:hidden">
      {areas.map(area => <div key={area.label}>
        <p className="py-2 text-xs font-semibold uppercase text-muted">{area.label}</p>
        {area.routes.map(route => <Link key={route.path} to={route.path} aria-current={routeActive(route.path) ? 'page' : undefined} className={`block ${linkClass(routeActive(route.path))}`}>{route.label}</Link>)}
      </div>)}
      <button onClick={handleLogout} className="mt-2 rounded-ctl px-2 py-1 text-sm">Abmelden</button>
    </nav>}
  </header>;
}
